import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, Copy, Loader2, QrCode, RefreshCw, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const P = '/integrations/cielo-gateway';
const FINAL_BAD = { NEGADO: 'Pagamento negado', CANCELADO: 'Cobrança cancelada na Cielo', ABORTADO: 'Pagamento abortado', NAO_FINALIZADO: 'Não finalizado', EXPIRADO: 'QR Code expirado', CANCELADO_LOCAL: 'Cobrança cancelada no caixa', ESTORNADO: 'Pagamento estornado', ERRO: 'Falha ao gerar cobrança' };
const errText = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'Erro na Cielo'; };

export const useCieloStatus = () => {
  const [st, setSt] = useState({ pix_enabled: false });
  useEffect(() => { api.get(`${P}/status`).then(r => setSt(r.data)).catch(() => {}); }, []);
  return st;
};

const Countdown = ({ expiresAt }) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000)));
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, [expiresAt]);
  return <span data-testid="cielo-pix-countdown" className="font-mono">{String(Math.floor(left / 60)).padStart(2, '0')}:{String(left % 60).padStart(2, '0')}</span>;
};

function ChargeForm({ info, amount, setAmount, name, setName, doc, setDoc }) {
  return <div className="space-y-3">
    <div className="p-3 bg-slate-50 rounded-lg text-sm flex justify-between"><span>Total {formatCurrency(info.total)} · Recebido {formatCurrency(info.paid)}</span><b data-testid="cielo-pix-balance">Saldo {formatCurrency(info.balance)}</b></div>
    <div><Label>Valor do PIX</Label><Input data-testid="cielo-pix-amount" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} /></div>
    <div><Label>Nome do comprador</Label><Input data-testid="cielo-pix-name" value={name} onChange={e => setName(e.target.value)} /></div>
    {info.identity_required
      ? <div><Label>CPF/CNPJ do comprador (exigido pela Cielo)</Label><Input data-testid="cielo-pix-document" inputMode="numeric" value={doc} onChange={e => setDoc(e.target.value)} placeholder="000.000.000-00" /></div>
      : <p className="text-xs text-slate-500" data-testid="cielo-pix-document-prefilled">Documento do cliente: {info.identity_type} {info.identity_masked}</p>}
  </div>;
}

function ChargeView({ charge, onRefresh, refreshing }) {
  const paid = charge.status === 'PAGO';
  if (paid) return <div className="text-center py-6" data-testid="cielo-pix-approved"><CheckCircle2 className="mx-auto text-emerald-600" size={64} /><p className="text-2xl font-bold text-emerald-700 mt-2">PAGAMENTO APROVADO</p><p className="text-slate-600">{formatCurrency(charge.amount_cents / 100)} · PIX Cielo</p>{charge.order && <p className="text-sm mt-1">Saldo do pedido: <b>{formatCurrency(charge.order.balance)}</b></p>}</div>;
  if (FINAL_BAD[charge.status]) return <div className="text-center py-6" data-testid="cielo-pix-failed"><XCircle className="mx-auto text-red-500" size={56} /><p className="text-lg font-semibold mt-2">{FINAL_BAD[charge.status]}</p><p className="text-xs text-slate-500">{charge.gateway_reason || charge.error_message}</p></div>;
  return <div className="space-y-3 text-center">
    <p className="text-3xl font-bold text-[#FF6B6B]" data-testid="cielo-pix-value">{formatCurrency(charge.amount_cents / 100)}</p>
    {charge.qr_code_base64 && <img data-testid="cielo-pix-qrcode" alt="QR Code PIX" className="mx-auto w-56 h-56 border rounded" src={`data:image/png;base64,${charge.qr_code_base64}`} />}
    {charge.qr_code_string && <div className="flex gap-2"><Input readOnly value={charge.qr_code_string} className="text-xs font-mono" data-testid="cielo-pix-copypaste" /><Button variant="outline" size="icon" data-testid="cielo-pix-copy" onClick={() => { navigator.clipboard?.writeText(charge.qr_code_string); toast.success('Código PIX copiado'); }}><Copy size={16} /></Button></div>}
    <div className="flex justify-center gap-4 text-sm text-slate-600"><span className="flex items-center gap-1"><Clock size={14} />Expira em {charge.expires_at && <Countdown expiresAt={charge.expires_at} />}</span><span data-testid="cielo-pix-status" className="flex items-center gap-1"><Loader2 size={14} className="animate-spin" />Aguardando pagamento</span></div>
    <p className="text-[11px] text-slate-400" data-testid="cielo-pix-ids">PaymentId {charge.payment_id} · txid {charge.sent_order_id}</p>
    <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="cielo-pix-check"><RefreshCw size={14} className="mr-1" />Consultar pagamento</Button>
    {charge.error_message && <p className="text-xs text-amber-600">{charge.error_message}</p>}
  </div>;
}

export function CieloPixDialog({ open, orderId, initialAmount, onClose, onPaid }) {
  const [info, setInfo] = useState(null);
  const [charge, setCharge] = useState(null);
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [doc, setDoc] = useState('');
  const [busy, setBusy] = useState(false);
  const idem = useRef(null);
  const paidNotified = useRef(false);

  useEffect(() => {
    if (!open || !orderId) return;
    setCharge(null); setInfo(null); paidNotified.current = false; idem.current = crypto.randomUUID();
    api.get(`${P}/orders/${orderId}/prefill`).then(r => {
      setInfo(r.data); setName(r.data.customer_name || ''); setDoc('');
      setAmount(String(initialAmount && initialAmount <= r.data.balance ? initialAmount : r.data.balance));
      if (r.data.active_charge) setCharge(r.data.active_charge);
    }).catch(e => toast.error(errText(e)));
  }, [open, orderId, initialAmount]);

  const refresh = useCallback(async (manual) => {
    if (!charge?.id) return;
    try { const r = await api.get(`${P}/payments/${charge.id}`, { params: { refresh: true } }); setCharge(r.data); }
    catch (e) { if (manual) toast.error(errText(e)); }
  }, [charge?.id]);

  const waiting = charge && ['PENDENTE', 'AUTORIZADO', 'CRIANDO'].includes(charge.status);
  useEffect(() => { if (!open || !waiting) return; const t = setInterval(() => refresh(false), 3000); return () => clearInterval(t); }, [open, waiting, refresh]);
  useEffect(() => { if (charge?.status === 'PAGO' && !paidNotified.current) { paidNotified.current = true; toast.success('PAGAMENTO APROVADO'); onPaid?.(charge); } }, [charge, onPaid]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${P}/pix`, { order_id: orderId, amount: parseFloat(amount), customer_name: name, customer_identity: info.identity_required ? doc : undefined, idempotency_key: idem.current });
      setCharge(r.data);
    } catch (e) {
      const d = e.response?.data?.detail; if (d?.active_charge) setCharge(d.active_charge);
      if (d?.identity_required) setInfo(i => ({ ...i, identity_required: true }));
      toast.error(errText(e)); idem.current = crypto.randomUUID();
    } finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { const r = await api.post(`${P}/payments/${charge.id}/cancel`); setCharge(r.data); toast('Cobrança cancelada no caixa. Se o cliente pagar depois, o sistema registra e sinaliza para revisão.'); }
    catch (e) { toast.error(errText(e)); refresh(false); } finally { setBusy(false); }
  };

  return <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
    <DialogContent className="max-w-md bg-white" data-testid="cielo-pix-dialog">
      <DialogHeader><DialogTitle><QrCode className="inline mr-2 text-[#FF6B6B]" size={20} />PIX Cielo</DialogTitle></DialogHeader>
      {!info ? <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>
        : charge ? <ChargeView charge={charge} onRefresh={() => refresh(true)} refreshing={busy} />
          : <ChargeForm {...{ info, amount, setAmount, name, setName, doc, setDoc }} />}
      <DialogFooter className="gap-2">
        {charge && waiting && <Button variant="outline" className="text-red-600" onClick={cancel} disabled={busy} data-testid="cielo-pix-cancel">Cancelar cobrança</Button>}
        {!charge && info && <Button onClick={create} disabled={busy || !(parseFloat(amount) > 0)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cielo-pix-generate">{busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <QrCode size={16} className="mr-1" />}Gerar QR Code PIX</Button>}
        {charge && !waiting && FINAL_BAD[charge.status] && <Button variant="outline" onClick={() => { setCharge(null); idem.current = crypto.randomUUID(); api.get(`${P}/orders/${orderId}/prefill`).then(r => { setInfo(r.data); setAmount(String(r.data.balance)); }); }} data-testid="cielo-pix-retry">Nova tentativa</Button>}
        <Button variant="outline" onClick={onClose} data-testid="cielo-pix-close">{charge?.status === 'PAGO' ? 'Concluir' : 'Fechar'}</Button>
      </DialogFooter>
      {waiting && <p className="text-[11px] text-slate-400 text-center">Pode fechar esta janela: a confirmação continua sendo verificada e aparece ao reabrir o PIX Cielo do pedido.</p>}
    </DialogContent>
  </Dialog>;
}
